import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "src/store";
import { DangerOutlinedButton, SecondaryButton } from "src/ui/shared/CustomButton";
import { makeStyles } from "tss-react/mui";

import { createDeviceMdmCommand } from "@dashboard/devices/api";
import apiClient from "@dashboard/files/api/apiClient";
import { setPaneTab } from "@devices/store";
import { Device } from "@devices/types";
import { ComplexCommandNames } from "@devices/types/commands";
import { FileInfo } from "@files/types";
import { Box } from "@mui/material";
import { CellContext } from "@tanstack/react-table";

import { Application } from "./AppTab";

const useStyles = makeStyles()((theme) => ({
    buttonBox: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: theme.spacing(1),
        width: 140,
        height: 50,
    },
}));

const getLatestApk = (avaibleApks: FileInfo[]) => {
    return avaibleApks.reduce((latest, current) => {
        const latestVersionCode = latest.metadata?.file?.apk?.manifest?.versioncode;
        const currentVersionCode = current.metadata?.file?.apk?.manifest?.versioncode;
        return latestVersionCode ? (Number(currentVersionCode) > Number(latestVersionCode) ? current : latest) : current;
    });
};
const getNewPackageVersion = (currentPackageVersion: number, avaibleApks: FileInfo[]) => {
    if (avaibleApks.length) {
        const latestApk = getLatestApk(avaibleApks);
        const latestPackageVersion = Number(latestApk.metadata?.file?.apk?.manifest?.versioncode);
        return latestPackageVersion > currentPackageVersion ? latestApk : null;
    }
    return null;
};

const ActionButtons = (props: CellContext<Application, unknown> & { device: Device; createLoading: boolean }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["sidepanels"]);

    const handleTooltipText = (): string => {
        if (!props.row.original.enabled) {
            return t("sidepanels:devicePanel.tooltip.applicationDisabled");
        } else if (!props.row.original.launchable) {
            return t("sidepanels:devicePanel.tooltip.applicationNotLaunchable");
        } else {
            return t("sidepanels:devicePanel.tooltip.startApplication");
        }
    };
    const handleStartButton = async () => {
        const startCommandObj = {
            command: ComplexCommandNames.StartPackage,
            caption: "Start package",
            package: props.row.original.package,
        };

        await createDeviceMdmCommand({ deviceId: props.device.id, json: startCommandObj }).then(() => {
            dispatch(setPaneTab("COMMANDS"));
        });
    };
    const handleStopButton = async () => {
        const stopCommandObj = {
            command: ComplexCommandNames.StopPackage,
            caption: "Stop package",
            package: props.row.original.package,
        };

        await createDeviceMdmCommand({ deviceId: props.device.id, json: stopCommandObj }).then(() => {
            dispatch(setPaneTab("COMMANDS"));
        });
    };

    if (props.row.original.enabled && props.row.original.launchable && !props.row.original.running) {
        return (
            <SecondaryButton
                tooltipProps={{ title: t("sidepanels:devicePanel.tooltip.startApplication") }}
                onClick={handleStartButton}
                disabled={props.createLoading}
                loading={props.createLoading}
            >
                <i className="fas fa-play" />
            </SecondaryButton>
        );
    } else if (props.row.original.launchable && props.row.original.running) {
        return (
            <SecondaryButton
                tooltipProps={{ title: t("sidepanels:devicePanel.tooltip.stopApplication") }}
                onClick={handleStopButton}
                disabled={props.createLoading}
                loading={props.createLoading}
            >
                <i className="fas fa-stop" />
            </SecondaryButton>
        );
    } else {
        return (
            <SecondaryButton disabled tooltipProps={{ title: handleTooltipText() }}>
                <i className="fas fa-play" />
            </SecondaryButton>
        );
    }
};
export const ActionCell = (props: CellContext<Application, unknown> & { device: Device; apks: API.File[] }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [createLoading, setCreateLoading] = useState(false);
    const { t } = useTranslation(["sidepanels", "common"]);

    const avaibleApks = props.apks.filter((item) => item.metadata?.file?.apk?.manifest?.package === props.row.original.package);
    const newPackageVersion = getNewPackageVersion(props.row.original.versionCode, avaibleApks);
    const isOffline = !props.device.isOnline;

    const installPackageForDevice = async (apk: FileInfo) => {
        setCreateLoading(true);
        try {
            const url = await apiClient.getPresignedDownloadUrl(apk.id);
            if (url) {
                const commandObject = {
                    url,
                    package: apk.metadata?.file?.apk?.manifest?.package,
                    version: apk.metadata?.file?.apk?.manifest?.versioncode,
                };

                await createDeviceMdmCommand({
                    deviceId: props.device.id,
                    json: { command: "installPackage", caption: "Install package", ...commandObject },
                }).then(() => {
                    dispatch(setPaneTab("COMMANDS"));
                });
            }
        } finally {
            setCreateLoading(false);
        }
    };
    const handleUninstallButton = async () => {
        setCreateLoading(true);
        try {
            const uninstallCommandObj = {
                command: ComplexCommandNames.UninstallPackage,
                caption: "Uninstall package",
                package: props.row.original.package,
                version: props.row.original.versionCode,
            };

            await createDeviceMdmCommand({ deviceId: props.device.id, json: uninstallCommandObj }).then(() => {
                dispatch(setPaneTab("COMMANDS"));
            });
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <Box style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className={classes.buttonBox}>
                {newPackageVersion && (
                    <SecondaryButton
                        onClick={() => installPackageForDevice(newPackageVersion)}
                        disabled={createLoading || isOffline}
                        loading={createLoading}
                        tooltipProps={{
                            title: isOffline
                                ? t("common:tooltip.deviceOffline")
                                : newPackageVersion.metadata?.file?.apk?.manifest?.versioncode
                                  ? t("sidepanels:devicePanel.tooltip.updateApplication", {
                                        version: newPackageVersion.metadata.file.apk.manifest.versioncode,
                                    })
                                  : t("sidepanels:devicePanel.tooltip.updateApplicationFallback"),
                        }}
                    >
                        <i className="fa-solid fa-angles-up" />{" "}
                    </SecondaryButton>
                )}

                <ActionButtons {...props} createLoading={createLoading} />

                {props.row.original.system === "application" && (
                    <DangerOutlinedButton
                        tooltipProps={{ title: t("sidepanels:devicePanel.tooltip.uninstallApp") }}
                        onClick={handleUninstallButton}
                        disabled={createLoading}
                        loading={createLoading}
                    >
                        <i className="fa-solid fa-trash-alt" />
                    </DangerOutlinedButton>
                )}
            </div>
        </Box>
    );
};
